// Utilidades de precios de DocEnLinea.
//
// El frontend NO decide precios: la tarifa (base, recargo nocturno/dominical y
// comisión) la define el backend y llega por GET /api/settings/public. Aquí
// solo se valida esa respuesta y se convierte en texto. Si no hay datos, los
// textos quedan neutros: nunca se muestra un valor inventado.

// Formatea un valor en pesos colombianos: 38000 -> "$ 38.000".
export const formatCOP = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(value);

// Texto neutro cuando todavía no hay tarifa (cargando o error de red).
export const PRICING_UNAVAILABLE_TEXT =
  'Consulta las tarifas vigentes al momento de agendar.';

const isAmount = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;

// Valida la tarifa que envía el backend. Devuelve null si falta algo esencial,
// para que la interfaz caiga en el estado neutro en lugar de mostrar un número
// a medias.
export const normalizePricing = (raw) => {
  if (!raw || !isAmount(raw.basePrice) || raw.basePrice <= 0) return null;
  const nightSurcharge = isAmount(raw.nightSurcharge) ? raw.nightSurcharge : 0;
  return {
    currency: raw.currency || 'COP',
    basePrice: raw.basePrice,
    nightSurcharge,
    nightTotal: isAmount(raw.nightTotal) ? raw.nightTotal : raw.basePrice + nightSurcharge,
    nightStart: raw.nightStart || null,
    nightEnd: raw.nightEnd || null,
    fullDaySurchargeDays: Array.isArray(raw.fullDaySurchargeDays)
      ? raw.fullDaySurchargeDays.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
      : [],
    doctorCommission: isAmount(raw.doctorCommission) ? raw.doctorCommission : null,
    timeZone: raw.timeZone || 'America/Bogota',
  };
};

// Índice de getDay() (0 = domingo) -> nombre en plural, como se usa en "los domingos".
const DAY_NAMES_PLURAL = [
  'domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados',
];

// "a, b y c"
const joinSpanish = (items) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;

// "los domingos" / "los sábados y los domingos". Cadena vacía si no hay días.
export const describeSurchargeDays = (days = []) => {
  const unique = [...new Set(days)].sort((a, b) => a - b);
  return joinSpanish(unique.map((d) => `los ${DAY_NAMES_PLURAL[d]}`));
};

const hasNightWindow = (p) => Boolean(p.nightStart && p.nightEnd);

// Describe CUÁNDO aplica el recargo: "entre las 19:00 y las 07:00 (hora de
// Colombia) y los domingos (todo el día)". Cadena vacía si no hay recargo.
export const describeSurchargeWhen = (pricing) => {
  const p = normalizePricing(pricing);
  if (!p || p.nightSurcharge <= 0) return '';
  const parts = [];
  if (hasNightWindow(p)) {
    parts.push(`entre las ${p.nightStart} y las ${p.nightEnd} (hora de Colombia)`);
  }
  const days = describeSurchargeDays(p.fullDaySurchargeDays);
  if (days) parts.push(`${days} (todo el día)`);
  return parts.join(' y ');
};

// Mensaje estándar de precios para pacientes, armado con los datos del backend.
export const describePricing = (pricing) => {
  const p = normalizePricing(pricing);
  if (!p) return PRICING_UNAVAILABLE_TEXT;

  const when = describeSurchargeWhen(p);
  if (!when) {
    return `El valor de la consulta es de ${formatCOP(p.basePrice)} COP a cualquier hora.`;
  }
  return (
    `El valor de la consulta es de ${formatCOP(p.basePrice)} COP en horario diurno. ` +
    `Las consultas ${when} tienen un recargo adicional de ${formatCOP(p.nightSurcharge)} COP ` +
    `que se suma al valor base, para un total de ${formatCOP(p.nightTotal)} COP.`
  );
};

// Neto que recibe el médico: valor de la consulta menos la comisión. El
// recargo completo es del médico. null si no hay datos suficientes.
export const doctorNet = (pricing, withSurcharge = false) => {
  const p = normalizePricing(pricing);
  if (!p || p.doctorCommission == null) return null;
  const gross = withSurcharge ? p.nightTotal : p.basePrice;
  return Math.max(gross - p.doctorCommission, 0);
};

// Texto de remuneración para médicos (términos y landing de médicos).
export const describeDoctorEarnings = (pricing) => {
  const p = normalizePricing(pricing);
  if (!p || p.doctorCommission == null) {
    return 'DocEnLinea retiene una comisión fija por consulta, según la tarifa vigente, y transfiere el saldo neto a la cuenta Nequi que registres.';
  }
  let text =
    `DocEnLinea retiene una comisión fija de ${formatCOP(p.doctorCommission)} COP por consulta ` +
    `y transfiere el saldo neto a la cuenta Nequi que registres: ${formatCOP(doctorNet(p))} COP ` +
    `por una consulta de ${formatCOP(p.basePrice)} COP`;
  const when = describeSurchargeWhen(p);
  if (when) {
    text +=
      ` y ${formatCOP(doctorNet(p, true))} COP por una consulta con recargo (${when}), ` +
      `porque el recargo de ${formatCOP(p.nightSurcharge)} COP se transfiere completo al médico`;
  }
  return `${text}.`;
};
